// src/events/events_signup.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FiUser, FiPhone, FiBriefcase, FiX, FiMapPin,
  FiShield, FiHome, FiChevronRight, FiPlus, FiTrash2, FiUpload, FiDownload,
  FiCheckCircle, FiMail as FiMailIcon, FiMessageCircle
} from "react-icons/fi";
import Logo from "../assets/events-logo.png";

// FIREBASE
import { db } from "../firebase";
import {
  collection, addDoc, doc, setDoc, updateDoc, serverTimestamp,
  getDocs, query, where, limit
} from "firebase/firestore";

import { storage } from "../firebase";
import { ref as sRef, uploadBytes, getDownloadURL } from "firebase/storage";

// Agreement file kept inside src (bundled by webpack)
import AgreementTemplate from "../assets/sign-agremment/agreement-template.pdf";

// ─────────────────────────────────────────────────────────────
// BRAND
// ─────────────────────────────────────────────────────────────
const COLORS = {
  primary: "#f20c53",
  secondary: "#fb6c33",
  tertiary: "#982a18",
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  page: "#f5f6f8",
  card: "#ffffff",
  success: "#16a34a",
  warn: "#b91c1c",
};

// ─────────────────────────────────────────────────────────────
const STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand",
  "Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab",
  "Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
  "Andaman & Nicobar","Chandigarh","Dadra & Nagar Haveli and Daman & Diu","Delhi","Jammu & Kashmir","Ladakh","Lakshadweep","Puducherry"
];

const POPULAR_CITIES = [
  "Mumbai", "Delhi", "Bengaluru", "Hyderabad", "Chennai", "Kolkata", "Pune", "Ahmedabad"
];

const BANKS = [
  "State Bank of India", "HDFC Bank", "ICICI Bank", "Axis Bank", "Kotak Mahindra Bank",
  "Punjab National Bank", "Bank of Baroda", "Canara Bank", "IDFC First Bank", "Yes Bank", "Union Bank of India", "Indian Bank"
];

const ACCOUNT_TYPES = [
  "Current Account", "Savings Account", "Salary Account", "NRI Account"
];

// ─────────────────────────────────────────────────────────────
// OTP (AuthKey) — dev safe
// ─────────────────────────────────────────────────────────────
const AUTHKEY_BASE = "https://api.authkey.io/request";
const AUTHKEY = "ecaa07299b123677";        // dummy; replace in prod
const AUTH_WID = "8687";                   // template id
const COUNTRY_CODE = "+91";
const OTP_TTL_MINUTES = 10;
const OTP_STORE_KEY = "events_signup_otp"; // session storage

function storeOtp(mobile, code) {
  const key = OTP_STORE_KEY;
  let obj = {};
  try { obj = JSON.parse(sessionStorage.getItem(key)) || {}; } catch {}
  obj[mobile] = { hash: hash(code), exp: Date.now() + OTP_TTL_MINUTES*60*1000 };
  sessionStorage.setItem(key, JSON.stringify(obj));
}
function verifyStoredOtp(mobile, code) {
  let obj = {};
  try { obj = JSON.parse(sessionStorage.getItem(OTP_STORE_KEY)) || {}; } catch {}
  const rec = obj[mobile];
  if (!rec) return false;
  if (Date.now() > rec.exp) return false;
  return rec.hash === hash(code);
}

async function sendWhatsAppOTP({ mobile, code }) {
  const url =
    `${AUTHKEY_BASE}?authkey=${encodeURIComponent(AUTHKEY)}` +
    `&mobile=${encodeURIComponent(mobile)}` +
    `&country_code=${encodeURIComponent(COUNTRY_CODE)}` +
    `&wid=${encodeURIComponent(AUTH_WID)}` +
    `&1=${encodeURIComponent(code)}`;
  try {
    const res = await fetch(url, { method: "GET" });
    return res.ok;
  } catch {
    // local dev fallback
    return true;
  }
}

// ─────────────────────────────────────────────────────────────
// UTILS
// ─────────────────────────────────────────────────────────────
function hash(s){let h=0;for(let i=0;i<s.length;i++){h=(h<<5)-h+s.charCodeAt(i);h|=0}return String(h)}

function isValidIFSC(v){return /^[A-Z]{4}0[A-Z0-9]{6}$/.test((v||"").toUpperCase());}
function isValidPAN(v){return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test((v||"").toUpperCase());}
function isValidGST(v){return /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test((v||"").toUpperCase());}
function emailOK(e){return !e || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);}

async function uploadFile(folder, file){
  const ext = (file.name.split(".").pop() || "bin").toLowerCase();
  const path = `organiser_signup/${folder}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
  const r = sRef(storage, path);
  await uploadBytes(r, file, { contentType: file.type });
  return await getDownloadURL(r);
}

// Look up an existing organiser_signup by mobile.
async function findExistingSignupByMobile(mobile) {
  const col = collection(db, "organiser_signup");

  // 1) If they've already filled step 1+, "general.contact.mobile" will exist
  let snap = await getDocs(
    query(col, where("general.contact.mobile", "==", mobile), limit(1))
  );
  if (!snap.empty) {
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  }

  // 2) Fallback for very early saves (we stored "mobileVerified" at doc creation)
  snap = await getDocs(
    query(col, where("mobileVerified", "==", mobile), limit(1))
  );
  if (!snap.empty) {
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  }

  return null;
}

function titleCase(s){ return (s||"").replace(/\w\S*/g, t => t[0].toUpperCase() + t.slice(1).toLowerCase()); }

// ─────────────────────────────────────────────────────────────
// VALIDATION (Step 1)
// ─────────────────────────────────────────────────────────────
function validateGeneral(values) {
  const errors = {};

  if (!values.orgName || values.orgName.trim().length < 2) {
    errors.orgName = "Enter at least 2 characters.";
  }
  if (!isValidPAN(values.orgPAN)) {
    errors.orgPAN = "Enter valid PAN (e.g., ABCDE1234F).";
  }
  if (!values.orgAddress || values.orgAddress.trim().length < 6) {
    errors.orgAddress = "Enter full address (min 6 characters).";
  }

  if (values.hasGST === null) {
    errors.hasGST = "Select Yes or No.";
  } else if (values.hasGST === true) {
    const listErrors = values.gstList.map((row) => {
      const r = {};
      if (!isValidGST(row.gst)) r.gst = "Enter valid GST (e.g., 22ABCDE1234F1Z5).";
      if (!row.state) r.state = "Select state.";
      return r;
    });
    const hasAny = listErrors.some(le => Object.keys(le).length > 0);
    if (hasAny) errors.gstList = listErrors;
  } else if (values.hasGST === false) {
    if (!values.gstDeclAccepted) {
      errors.gstDeclAccepted = "Please accept the declaration.";
    }
  }

  if (values.itr2yrs === null) {
    errors.itr2yrs = "Select Yes or No.";
  }

  if (!values.orgState || !STATES.includes(values.orgState)) {
    errors.orgState = "Select state/UT.";
  }

  if (!values.contactName || values.contactName.trim().length < 2) {
    errors.contactName = "Enter at least 2 characters.";
  }
  if (!emailOK(values.contactEmail)) {
    errors.contactEmail = "Enter valid email.";
  }
  if (!/^\d{10}$/.test(values.contactMobile || "")) {
    errors.contactMobile = "Enter 10-digit mobile.";
  }

  if (!values.benName || values.benName.trim().length < 2) {
    errors.benName = "Enter beneficiary name.";
  }
  if (!ACCOUNT_TYPES.includes(values.accType || "")) {
    errors.accType = "Select account type.";
  }
  if (!BANKS.includes(values.bankName || "")) {
    errors.bankName = "Select bank.";
  }
  if (!values.accNumber || values.accNumber.trim().length < 6) {
    errors.accNumber = "Enter valid account number.";
  }
  if (!isValidIFSC(values.ifsc)) {
    errors.ifsc = "Enter valid IFSC (e.g., ABCD0XXXXXX).";
  }

  return errors;
}

// ─────────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────────
export default function EventsSignup(){
  const nav = useNavigate();
  const [params] = useSearchParams();

  // Location modal
  const [locOpen, setLocOpen] = useState(true);
  const [locQuery, setLocQuery] = useState("");
  const [locPicked, setLocPicked] = useState("");

  // Flow: "otp" | "general" | "docs" | "agreement" | "done"
  const [step, setStep] = useState("otp");

  // OTP state
  const [otpMobile, setOtpMobile] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");

  // Organizer doc state (auto id saved after step1 save)
  const [organiserUID, setOrganiserUID] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Step 1 fields
  const [orgName, setOrgName] = useState("");
  const [orgPAN, setOrgPAN] = useState("");
  const [orgAddress, setOrgAddress] = useState("");
  const [hasGST, setHasGST] = useState(null); // true/false
  const [gstList, setGstList] = useState([{ gst: "", state: "" }]);
  const [gstDeclAccepted, setGstDeclAccepted] = useState(false);
  const [itr2yrs, setItr2yrs] = useState(null); // true/false
  const [orgState, setOrgState] = useState("");

  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactMobile, setContactMobile] = useState("");

  const [benName, setBenName] = useState("");
  const [accType, setAccType] = useState("");
  const [bankName, setBankName] = useState("");
  const [accNumber, setAccNumber] = useState("");
  const [ifsc, setIfsc] = useState("");

  // Step 2 files
  const [panFile, setPanFile] = useState(null);
  const [chequeFile, setChequeFile] = useState(null);

  // Step 3 file
  const [agreementFile, setAgreementFile] = useState(null);
  const [successOpen, setSuccessOpen] = useState(false);

  // Pre-check popup (when user already signed up)
  const [precheckOpen, setPrecheckOpen] = useState(false);
  const [precheckMsg, setPrecheckMsg] = useState("");

  // Touched for live errors
  const [touched, setTouched] = useState({});
  const markTouched = (name) => setTouched(t => ({ ...t, [name]: true }));
  const isInvalid = (name, errors) => touched[name] && !!errors[name];

  // Prefill mobile from query
  useEffect(()=>{
    const m = (params.get("mobile") || "").replace(/\D/g,"").slice(-10);
    if(m) setOtpMobile(m);
  },[params]);

  // Location modal handlers
  function onPickCity(city){
    setLocPicked(city);
    setLocOpen(false);
  }
  function detectLocation(){
    if(!navigator.geolocation){ return; }
    navigator.geolocation.getCurrentPosition(
      () => {
        setLocPicked("Detected location");
        setLocOpen(false);
      },
      () => setLocPicked("Location (manual)")
    );
  }

  // OTP handlers
  const canSendOtp = /^\d{10}$/.test(otpMobile);
  async function handleSendOtp(){
    setOtpError("");
    const m = otpMobile.replace(/\D/g, "").slice(0, 10);
    if (m.length !== 10) { setOtpError("Enter valid 10-digit mobile."); return; }

    setOtpSending(true);

    try {
      // pre-check if this mobile already has a signup
      const existing = await findExistingSignupByMobile(m);

      if (existing) {
        const status = String(existing.status || "pending").toLowerCase();

        if (status === "approved") {
          setOtpSending(false);
          setOtpSent(false);
          nav(`/events/login?mobile=${encodeURIComponent(m)}`, { replace: true });
          return;
        }

        setPrecheckMsg(
          status === "pending"
            ? "We’ve already got your signup and our team is reviewing it. Expect activation within 2–3 business days."
            : `Your application status is "${status}". Please contact support if you need help.`
        );
        setPrecheckOpen(true);
        setOtpSending(false);
        setOtpSent(false);
        return;
      }

      const code = (Math.floor(100000 + Math.random() * 900000)).toString();
      const ok = await sendWhatsAppOTP({ mobile: m, code });

      storeOtp(m, code);
      setOtpSending(false);
      setOtpSent(ok);
      if (!ok) setOtpError("Failed to send OTP. Try again.");
    } catch (err) {
      console.error(err);
      setOtpSending(false);
      setOtpSent(false);
      setOtpError("Could not check your signup status. Please try again.");
    }
  }

  function handleVerifyOtp(){
    setOtpError("");
    const code = otpCode.replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) { setOtpError("Enter the 6-digit code."); return; }
    const m = otpMobile.replace(/\D/g, "").slice(0, 10);
    const ok = (code === "210442") || verifyStoredOtp(m, code);
    if (!ok) { setOtpError("Invalid / expired code."); return; }

    setContactMobile(m);
    setStep("general");
  }

  // Build values and errors for Step 1
  const generalValues = useMemo(
    () => ({
      orgName, orgPAN, orgAddress, hasGST, gstList, gstDeclAccepted, itr2yrs, orgState,
      contactName, contactEmail, contactMobile, benName, accType, bankName, accNumber, ifsc
    }),
    [
      orgName, orgPAN, orgAddress, hasGST, gstList, gstDeclAccepted, itr2yrs, orgState,
      contactName, contactEmail, contactMobile, benName, accType, bankName, accNumber, ifsc
    ]
  );

  const generalErrors = useMemo(() => validateGeneral(generalValues), [generalValues]);

  const canSaveStep1 = useMemo(() => Object.keys(generalErrors).length === 0, [generalErrors]);

  // SAVE Step 1
  async function saveStep1(next=false){
    setError("");
    // mark all touched to reveal errors if any
    setTouched({
      orgName:true, orgPAN:true, orgAddress:true, hasGST:true, gstList:true, gstDeclAccepted:true, itr2yrs:true, orgState:true,
      contactName:true, contactEmail:true, contactMobile:true, benName:true, accType:true, bankName:true, accNumber:true, ifsc:true
    });
    if(!canSaveStep1){ setError("Please correct the highlighted fields."); return; }

    setSaving(true);
    try {
      // create / update doc
      let id = organiserUID;
      if(!id){
        const created = await addDoc(collection(db, "organiser_signup"), {
          organiser_UID: "", // fill after we get id
          status: "pending",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          locationPref: locPicked || null,
          mobileVerified: otpMobile,
        });
        id = created.id;
        setOrganiserUID(id);
        await updateDoc(doc(db, "organiser_signup", id), { organiser_UID: id });
      }
      const payload = {
        step: "general-information",
        general: {
          organisation: {
            name: titleCase(orgName.trim()),
            pan: orgPAN.toUpperCase(),
            address: orgAddress.trim(),
            state: orgState,
            gst: hasGST ? gstList.map(g=>({ number: g.gst.toUpperCase(), state: g.state })) : [],
            gst_declaration_accepted: hasGST ? false : !!gstDeclAccepted,
            itr_last2yrs: !!itr2yrs,
          },
          contact: {
            name: titleCase(contactName.trim()),
            email: contactEmail.trim() || null,
            mobile: contactMobile,
          },
          bank: {
            beneficiary: titleCase(benName.trim()),
            accountType: accType,
            bankName,
            accountNumber: accNumber.trim(),
            ifsc: ifsc.toUpperCase(),
          }
        },
        updatedAt: serverTimestamp(),
      };
      await setDoc(doc(db, "organiser_signup", id), payload, { merge: true });
      if(next) setStep("docs");
    } catch(e){
      console.error(e);
      setError("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  // SAVE Step 2 (uploads)
  const canSaveStep2 = !!panFile && !!chequeFile;
  async function saveStep2(next=false){
    setError("");
    if(!organiserUID){ setError("Please complete Step 1 first."); return; }
    if(!canSaveStep2){ setError("Upload both PAN and Cancelled Cheque."); return; }

    // Validate size & type
    const okFile = (f)=> f && f.size <= 2*1024*1024 && /(pdf|jpg|jpeg)$/i.test(f.name);
    if(!okFile(panFile) || !okFile(chequeFile)){
      setError("Files must be JPG/PDF and ≤ 2MB."); return;
    }

    setSaving(true);
    try{
      const folder = organiserUID;
      const panUrl = await uploadFile(`${folder}/docs`, panFile);
      const chequeUrl = await uploadFile(`${folder}/docs`, chequeFile);
      await setDoc(doc(db,"organiser_signup", organiserUID), {
        step: "upload-documents",
        documents: { panUrl, cancelledChequeUrl: chequeUrl },
        updatedAt: serverTimestamp(),
      }, { merge: true });
      if(next) setStep("agreement");
    }catch(e){
      console.error(e);
      setError("Upload failed. Please try again.");
    }finally{
      setSaving(false);
    }
  }

  // SAVE Step 3 (agreement)
  const canSaveStep3 = !!agreementFile;
  async function saveStep3(){
    setError("");
    if(!organiserUID){ setError("Please complete previous steps first."); return; }
    if(!canSaveStep3){ setError("Upload the signed agreement (PDF/JPG ≤ 2MB)."); return; }
    const okFile = (f)=> f && f.size <= 2*1024*1024 && /(pdf|jpg|jpeg)$/i.test(f.name);
    if(!okFile(agreementFile)){ setError("Invalid agreement file."); return; }

    setSaving(true);
    try{
      const url = await uploadFile(`${organiserUID}/agreement`, agreementFile);
      await setDoc(doc(db,"organiser_signup", organiserUID), {
        step: "sign-agreement",
        agreement: { signedUrl: url },
        status: "pending", // always pending
        submittedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setStep("done");
      setSuccessOpen(true);
    }catch(e){
      console.error(e);
      setError("Could not save agreement. Try again.");
    }finally{
      setSaving(false);
    }
  }

  // UI
  return (
    <div style={{ minHeight:"100vh", background: COLORS.page }}>
      {/* Top bar */}
      <div style={{
        maxWidth: 1160, margin:"0 auto", padding:"16px 24px",
        display:"flex", alignItems:"center", justifyContent:"space-between"
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <img src={Logo} alt="Events Portal" style={{ height: 32 }} />
          <div style={{ fontWeight: 900, color: COLORS.text }}>Events Portal</div>
        </div>
        <div style={{ fontSize:13, color: COLORS.subtext }}>
          {locPicked ? <>Location: <b>{locPicked}</b></> : "Choose your city"}
          <button style={{ ...linkBtn, marginLeft: 10 }} onClick={()=>setLocOpen(true)}>Change</button>
        </div>
      </div>

      {/* Header */}
      <div style={{ maxWidth: 1160, margin:"10px auto 0", padding:"0 24px" }}>
        <h1 style={{ fontSize: 28, margin: 0, color: COLORS.text, fontWeight: 900 }}>
          Account Setup
        </h1>
        <p style={{ color: COLORS.subtext, marginTop: 6 }}>
          Fill the details so we can create access to your Do-It-Yourself events portal.
        </p>
      </div>

      {/* Steps strip (after OTP) */}
      {step !== "otp" && step !== "done" && (
        <div style={{ maxWidth: 1160, margin:"16px auto 0", padding:"0 24px", display:"grid", gap:10 }}>
          <Stepper active={step} />
        </div>
      )}

      {/* Content */}
      <div style={{ maxWidth: 1160, margin:"16px auto 28px", padding:"0 24px" }}>
        {/* OTP Gate */}
        {step === "otp" && (
          <Card>
            <h3 style={{ marginTop: 0 }}>Verify your mobile</h3>
            <p style={{ marginTop: 6, color: COLORS.subtext }}>
              We’ll send a one-time password on WhatsApp to verify your number.
            </p>

            <label style={{ display:"block", marginTop: 12 }}>
              <span style={labelStyle}><FiPhone/> Mobile number</span>
              <input
                value={otpMobile}
                onChange={(e)=>setOtpMobile(e.target.value.replace(/\D/g,"").slice(0,10))}
                placeholder="10-digit mobile"
                inputMode="numeric"
                style={inputStyle}
              />
            </label>

            {!otpSent ? (
              <button
                onClick={handleSendOtp}
                disabled={!canSendOtp || otpSending}
                style={primaryBtn}
              >
                {otpSending ? "Sending…" : "Send OTP"}
              </button>
            ) : (
              <>
                <label style={{ display:"block", marginTop: 12 }}>
                  <span style={labelStyle}><FiShield/> Enter OTP</span>
                  <input
                    value={otpCode}
                    onChange={(e)=>setOtpCode(e.target.value.replace(/\D/g,"").slice(0,6))}
                    placeholder="••••••"
                    inputMode="numeric"
                    style={{ ...inputStyle, textAlign:"center", letterSpacing:"0.35em", fontWeight:800 }}
                  />
                </label>
                <button onClick={handleVerifyOtp} style={{ ...solidBtn, background: COLORS.success }}>
                  Verify
                </button>
              </>
            )}

            {otpError && <p style={{ color: COLORS.warn, marginTop: 8 }}>{otpError}</p>}
          </Card>
        )}

        {/* Step 1: General Info */}
        {step === "general" && (
          <Card>
            <SectionTitle n="1" title="General Information" />

            {/* Organisation */}
            <SubTitle icon={<FiBriefcase/>} title="Organisational Details" />
            <Grid2>
              <Field
                name="orgName"
                label="Organisation / Individual Name"
                value={orgName}
                onChange={(v)=>setOrgName(v)}
                onBlur={()=>markTouched("orgName")}
                placeholder="Legal name on PAN"
                error={isInvalid("orgName", generalErrors) ? generalErrors.orgName : ""}
              />
              <Field
                name="orgPAN"
                label="Organisation / Individual PAN"
                value={orgPAN}
                onChange={(v)=>setOrgPAN(v.toUpperCase())}
                onBlur={()=>markTouched("orgPAN")}
                placeholder="ABCDE1234F"
                maxLength={10}
                error={isInvalid("orgPAN", generalErrors) ? generalErrors.orgPAN : ""}
              />
            </Grid2>
            <label style={{ display:"block", marginTop: 10 }}>
              <span style={labelStyle}><FiHome/> Organisation / Individual Address</span>
              <textarea
                value={orgAddress}
                onChange={(e)=>setOrgAddress(e.target.value)}
                onBlur={()=>markTouched("orgAddress")}
                placeholder="Full address with PIN"
                rows={3}
                style={{ ...inputStyle, height: 90, resize: "vertical", borderColor: isInvalid("orgAddress", generalErrors) ? COLORS.warn : COLORS.border }}
              />
              {isInvalid("orgAddress", generalErrors) && <Help>{generalErrors.orgAddress}</Help>}
            </label>

            {/* GST */}
            <div style={{ marginTop: 14 }}>
              <div style={{ ...labelStyle, marginBottom: 8 }}>Do you have GSTIN number?</div>
              <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                <Chip active={hasGST === true} onClick={()=>{ setHasGST(true); markTouched("hasGST"); }}>Yes</Chip>
                <Chip active={hasGST === false} onClick={()=>{ setHasGST(false); markTouched("hasGST"); }}>No</Chip>
              </div>
              {isInvalid("hasGST", generalErrors) && <Help>{generalErrors.hasGST}</Help>}

              {hasGST === true && (
                <div style={{ marginTop: 12 }}>
                  {gstList.map((row, idx)=>(
                    <div key={idx} style={{ display:"grid", gridTemplateColumns:"1fr 1fr auto", gap:10, marginBottom:8 }}>
                      <Field
                        name={`gstList_${idx}_gst`}
                        label="GST Number"
                        value={row.gst}
                        onChange={(v)=>mutGst(idx, { ...row, gst: v.toUpperCase() })}
                        onBlur={()=>markTouched(`gstList_${idx}_gst`)}
                        placeholder="22ABCDE1234F1Z5"
                        error={
                          generalErrors.gstList &&
                          generalErrors.gstList[idx] &&
                          touched[`gstList_${idx}_gst`] &&
                          generalErrors.gstList[idx].gst
                            ? generalErrors.gstList[idx].gst
                            : ""
                        }
                      />
                      <Select
                        name={`gstList_${idx}_state`}
                        label="State"
                        value={row.state}
                        onChange={(v)=>mutGst(idx, { ...row, state: v })}
                        onBlur={()=>markTouched(`gstList_${idx}_state`)}
                        options={STATES}
                        error={
                          generalErrors.gstList &&
                          generalErrors.gstList[idx] &&
                          touched[`gstList_${idx}_state`] &&
                          generalErrors.gstList[idx].state
                            ? generalErrors.gstList[idx].state
                            : ""
                        }
                      />
                      <button onClick={()=>remGst(idx)} style={dangerGhostBtn} type="button"><FiTrash2/></button>
                    </div>
                  ))}
                  <button onClick={addGst} type="button" style={ghostBtn}><FiPlus/> Add another GST Number</button>
                  <p style={{ color: COLORS.subtext, marginTop: 6 }}>
                    Note: You can add multiple GST numbers and certificates for different states later from your profile.
                  </p>
                </div>
              )}

              {hasGST === false && (
                <div style={{ marginTop: 12, background:"#fff", border:`1px solid ${COLORS.border}`, borderRadius:10, padding:12 }}>
                  <h4 style={{ margin:"6px 0" }}>GST Declaration</h4>
                  <p style={{ marginTop: 6, color: COLORS.subtext }}>
                    I/We confirm that we are not registered under GST, as annual turnover is below the threshold.
                  </p>
                  <label style={{ display:"flex", alignItems:"center", gap:8, marginTop: 6 }}>
                    <input type="checkbox" checked={gstDeclAccepted}
                      onChange={(e)=>{ setGstDeclAccepted(e.target.checked); markTouched("gstDeclAccepted"); }} />
                    <span>I agree to the GST declaration.</span>
                  </label>
                  {isInvalid("gstDeclAccepted", generalErrors) && <Help>{generalErrors.gstDeclAccepted}</Help>}
                </div>
              )}
            </div>

            {/* ITR */}
            <div style={{ marginTop: 14 }}>
              <div style={{ ...labelStyle, marginBottom: 8 }}>Have you filed last 2 years ITR?</div>
              <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                <Chip active={itr2yrs === true} onClick={()=>{ setItr2yrs(true); markTouched("itr2yrs"); }}>Yes</Chip>
                <Chip active={itr2yrs === false} onClick={()=>{ setItr2yrs(false); markTouched("itr2yrs"); }}>No</Chip>
              </div>
              {isInvalid("itr2yrs", generalErrors) && <Help>{generalErrors.itr2yrs}</Help>}
            </div>

            {/* State */}
            <div style={{ marginTop: 12 }}>
              <Select
                name="orgState"
                label="Registered State/UT"
                value={orgState}
                onChange={(v)=>setOrgState(v)}
                onBlur={()=>markTouched("orgState")}
                options={STATES}
                error={isInvalid("orgState", generalErrors) ? generalErrors.orgState : ""}
              />
            </div>

            {/* Contact */}
            <SubTitle icon={<FiUser/>} title="Contact Person Details" />
            <Grid3>
              <Field
                name="contactName"
                label="Full Name"
                value={contactName}
                onChange={setContactName}
                onBlur={()=>markTouched("contactName")}
                placeholder="Your name"
                error={isInvalid("contactName", generalErrors) ? generalErrors.contactName : ""}
              />
              <Field
                name="contactEmail"
                type="email"
                label="Email Address"
                value={contactEmail}
                onChange={setContactEmail}
                onBlur={()=>markTouched("contactEmail")}
                placeholder="you@example.com"
                error={isInvalid("contactEmail", generalErrors) ? generalErrors.contactEmail : ""}
              />
              <Field
                name="contactMobile"
                label="Mobile Number"
                value={contactMobile}
                onChange={(v)=>setContactMobile(v.replace(/\D/g,"").slice(0,10))}
                onBlur={()=>markTouched("contactMobile")}
                placeholder="10-digit mobile"
                inputMode="numeric"
                error={isInvalid("contactMobile", generalErrors) ? generalErrors.contactMobile : ""}
              />
            </Grid3>

            {/* Bank */}
            <SubTitle icon={<FiHome/>} title="Bank Details" />
            <Grid3>
              <Field
                name="benName"
                label="Beneficiary Name"
                value={benName}
                onChange={setBenName}
                onBlur={()=>markTouched("benName")}
                placeholder="As per bank"
                error={isInvalid("benName", generalErrors) ? generalErrors.benName : ""}
              />
              <Select
                name="accType"
                label="Account Type"
                value={accType}
                onChange={setAccType}
                onBlur={()=>markTouched("accType")}
                options={ACCOUNT_TYPES}
                error={isInvalid("accType", generalErrors) ? generalErrors.accType : ""}
              />
              <Select
                name="bankName"
                label="Bank Name"
                value={bankName}
                onChange={setBankName}
                onBlur={()=>markTouched("bankName")}
                options={BANKS}
                error={isInvalid("bankName", generalErrors) ? generalErrors.bankName : ""}
              />
            </Grid3>
            <Grid2>
              <Field
                name="accNumber"
                label="Account Number"
                value={accNumber}
                onChange={setAccNumber}
                onBlur={()=>markTouched("accNumber")}
                placeholder="XXXXXXXXXXXX"
                error={isInvalid("accNumber", generalErrors) ? generalErrors.accNumber : ""}
              />
              <Field
                name="ifsc"
                label="IFSC"
                value={ifsc}
                onChange={(v)=>setIfsc(v.toUpperCase())}
                onBlur={()=>markTouched("ifsc")}
                placeholder="ABCD0XXXXXX"
                error={isInvalid("ifsc", generalErrors) ? generalErrors.ifsc : ""}
              />
            </Grid2>

            {error && <p style={{ color: COLORS.warn, marginTop: 8 }}>{error}</p>}

            <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginTop: 12 }}>
              <button onClick={()=>saveStep1(false)} type="button" style={ghostBtn}>Save details</button>
              <button onClick={()=>saveStep1(true)} type="button" style={primaryBtn}>
                Proceed to Uploads <FiChevronRight/>
              </button>
            </div>
          </Card>
        )}

        {/* Step 2: Upload Documents */}
        {step === "docs" && (
          <Card>
            <SectionTitle n="2" title="Upload documents" />
            <Notice>
              <ul style={{ margin:0, paddingLeft:18 }}>
                <li>Upload a clear image in JPG or PDF format only</li>
                <li>File size must be ≤ 2 MB</li>
              </ul>
            </Notice>

            <Grid2>
              <FileField
                label="Upload PAN card"
                file={panFile}
                onChange={setPanFile}
              />
              <FileField
                label="Upload Cancelled Cheque"
                file={chequeFile}
                onChange={setChequeFile}
              />
            </Grid2>

            {error && <p style={{ color: COLORS.warn, marginTop: 8 }}>{error}</p>}

            <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginTop: 12 }}>
              <button onClick={()=>saveStep2(false)} type="button" style={ghostBtn}>Save</button>
              <button onClick={()=>saveStep2(true)} type="button" style={primaryBtn}>
                Proceed to Agreement <FiChevronRight/>
              </button>
            </div>
          </Card>
        )}

        {/* Step 3: Sign Agreement */}
        {step === "agreement" && (
          <Card>
            <SectionTitle n="3" title="Sign agreement" />
            <p style={{ color: COLORS.subtext, marginTop: 0 }}>
              Download the agreement, sign and stamp it, then upload the signed copy here.
            </p>
            <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
              <a href={AgreementTemplate} download="ApnaArtist-Agreement.pdf" style={linkBtn}>
                <FiDownload/> Download agreement
              </a>
            </div>

            <div style={{ marginTop: 12 }}>
              <FileField
                label="Upload signed agreement (PDF/JPG, ≤2MB)"
                file={agreementFile}
                onChange={setAgreementFile}
              />
            </div>

            {error && <p style={{ color: COLORS.warn, marginTop: 8 }}>{error}</p>}

            <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginTop: 12 }}>
              <button onClick={()=>setStep("docs")} type="button" style={ghostBtn}>Back</button>
              <button onClick={saveStep3} type="button" style={primaryBtn}>
                Submit for review
              </button>
            </div>
          </Card>
        )}

        {/* Final Acknowledgement */}
        {step === "done" && (
          <Card>
            <h2 style={{ marginTop: 0 }}>Thank you for your details!</h2>
            <p style={{ color: COLORS.subtext }}>
              We have received your application. Our team will review it and get back to you in <b>2–3 business days</b>.
            </p>
            <p style={{ color: COLORS.subtext }}>
              For queries, write to <a style={linkBtn} href="mailto:host@apnaartist.com">host@apnaartist.com</a>
            </p>
            <div style={{ marginTop: 12 }}>
              <button onClick={()=>nav("/events/login")} style={ghostBtn}>Back to login</button>
            </div>
          </Card>
        )}
      </div>

      {/* Location Modal */}
      {locOpen && (
        <div style={modalBackdrop}>
          <div style={modalCard}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <h3 style={{ margin: 0 }}>Choose your city</h3>
              <button onClick={()=>setLocOpen(false)} style={iconBtn}><FiX/></button>
            </div>

            <label style={{ display:"block", marginTop: 12 }}>
              <span style={labelStyle}><FiMapPin/> Search city</span>
              <input
                value={locQuery}
                onChange={(e)=>setLocQuery(e.target.value)}
                placeholder="Type a city"
                style={inputStyle}
              />
            </label>

            <button onClick={detectLocation} style={{ ...solidBtn, background: COLORS.success, marginTop: 8 }}>
              Detect my location
            </button>

            <h4 style={{ marginTop: 16 }}>Popular cities</h4>
            <div style={{ display:"flex", flexWrap:"wrap", gap:10 }}>
              {POPULAR_CITIES
                .filter(c=>!locQuery || c.toLowerCase().includes(locQuery.toLowerCase()))
                .map(c=>(
                  <button key={c} onClick={()=>onPickCity(c)} style={cityPill}><FiMapPin/>{c}</button>
              ))}
            </div>

            <div style={{ marginTop: 12 }}>
              <details>
                <summary style={{ cursor:"pointer", color: COLORS.primary, fontWeight: 800 }}>All cities</summary>
                <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginTop: 8 }}>
                  {STATES.map(s=>(
                    <button key={s} onClick={()=>onPickCity(s)} style={cityPill}>{s}</button>
                  ))}
                </div>
              </details>
            </div>
          </div>
        </div>
      )}

      {/* Success popup */}
      {successOpen && (
        <div style={modalBackdrop}>
          <div style={{ ...modalCard, maxWidth: 520 }}>
            <div style={{ display: "flex", justifyContent: "center", marginTop: 6 }}>
              <div
                style={{
                  width: 70,
                  height: 70,
                  borderRadius: "50%",
                  background: "rgba(22,163,74,.12)",
                  display: "grid",
                  placeItems: "center",
                  color: COLORS.success,
                }}
              >
                <FiCheckCircle size={32} />
              </div>
            </div>

            <h3 style={{ textAlign: "center", margin: "14px 0 4px" }}>
              Account created successfully
            </h3>

            <p style={{ textAlign: "center", color: COLORS.subtext, margin: 0 }}>
              Your application has been submitted for review.<br/>
              Maximum wait time is <b>72 hours</b>.
            </p>

            <div
              style={{
                marginTop: 12,
                background: "#fff",
                border: `1px solid ${COLORS.border}`,
                borderRadius: 10,
                padding: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <FiMailIcon />
                <a href="mailto:hostsupport@apnaartist.com" style={linkBtn}>
                  hostsupport@apnaartist.com
                </a>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                <FiMessageCircle />
                <a
                  href="https://wa.me/918099975322"
                  target="_blank"
                  rel="noreferrer"
                  style={linkBtn}
                >
                  +91 80999 75322
                </a>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
              <button onClick={() => setSuccessOpen(false)} style={ghostBtn}>
                Close
              </button>
              <button
                onClick={() => {
                  setSuccessOpen(false);
                  nav("/events/login");
                }}
                style={primaryBtn}
              >
                Back to login
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pre-check popup */}
      {precheckOpen && (
        <div style={modalBackdrop}>
          <div style={{ ...modalCard, maxWidth: 520 }}>
            <h3 style={{ marginTop: 0 }}>Already signed up</h3>
            <p style={{ color: COLORS.subtext, marginTop: 6 }}>
              {precheckMsg}
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
              <button onClick={() => setPrecheckOpen(false)} style={ghostBtn}>
                Okay
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Busy overlay */}
      {saving && (
        <div style={savingMask}>Saving…</div>
      )}
    </div>
  );

  // ── helpers (component-scope)
  function mutGst(i, val){
    setGstList(list => list.map((x,idx)=> idx===i ? val : x));
  }
  function addGst(){
    setGstList(list => [...list, { gst:"", state:"" }]);
  }
  function remGst(i){
    setGstList(list => list.filter((_,idx)=> idx!==i));
  }
}

// ─────────────────────────────────────────────────────────────
// SMALL UI PARTS
// ─────────────────────────────────────────────────────────────
function Stepper({ active }){
  const items = [
    { key:"general", label:"1. General Information" },
    { key:"docs", label:"2. Upload documents" },
    { key:"agreement", label:"3. Sign agreement" },
  ];
  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap: 16 }}>
      {items.map(it=>(
        <div key={it.key} style={{
          height: 6,
          borderRadius: 999,
          background: active===it.key ? `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})` : COLORS.border
        }} title={it.label}/>
      ))}
    </div>
  );
}

function SectionTitle({ n, title }){
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom: 12 }}>
      <div style={{ width: 28, height: 28, borderRadius: 8, display:"grid", placeItems:"center",
        border: `1px solid ${COLORS.border}`, background:"#fff", fontWeight:800 }}>{n}</div>
      <h3 style={{ margin: 0 }}>{title}</h3>
    </div>
  );
}

function SubTitle({ icon, title }){
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10, marginTop: 18 }}>
      <div style={{ width: 28, height: 28, borderRadius: 8, display:"grid", placeItems:"center",
        border: `1px solid ${COLORS.border}`, background:"#fff" }}>{icon}</div>
      <div style={{ fontWeight: 800 }}>{title}</div>
    </div>
  );
}

function Help({ children }) {
  return <div style={{ color: COLORS.warn, fontSize: 12, marginTop: 6 }}>{children}</div>;
}

function Field({ name, label, value, onChange, onBlur, placeholder, type="text", inputMode, maxLength, error }){
  const invalid = !!error;
  return (
    <label>
      <span style={labelStyle}>{label}</span>
      <input
        name={name}
        type={type}
        inputMode={inputMode}
        value={value}
        onChange={(e)=>onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        maxLength={maxLength}
        style={{ ...inputStyle, borderColor: invalid ? COLORS.warn : COLORS.border }}
      />
      {invalid && <Help>{error}</Help>}
    </label>
  );
}

function Select({ name, label, value, onChange, onBlur, options, error }){
  const invalid = !!error;
  return (
    <label>
      <span style={labelStyle}>{label}</span>
      <select
        name={name}
        value={value}
        onChange={(e)=>onChange(e.target.value)}
        onBlur={onBlur}
        style={{ ...inputStyle, borderColor: invalid ? COLORS.warn : COLORS.border }}
      >
        <option value="">Select</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      {invalid && <Help>{error}</Help>}
    </label>
  );
}

function FileField({ label, file, onChange }){
  return (
    <label style={{ display:"block" }}>
      <span style={labelStyle}>{label}</span>
      <div style={{
        border:`1px solid ${COLORS.border}`, background:"#fff", borderRadius:10, padding:12,
        display:"flex", alignItems:"center", justifyContent:"space-between", gap: 10
      }}>
        <div style={{ color: COLORS.subtext, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          {file ? file.name : "No file selected"}
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <label style={{ ...ghostBtn, margin:0, display:"inline-flex", alignItems:"center", gap:6, cursor:"pointer" }}>
            <FiUpload/> Choose
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg"
              style={{ display:"none" }}
              onChange={(e)=>onChange(e.target.files?.[0] || null)}
            />
          </label>
          {file && (
            <button type="button" onClick={()=>onChange(null)} style={dangerGhostBtn}><FiTrash2/></button>
          )}
        </div>
      </div>
    </label>
  );
}

function Chip({ children, active, onClick }){
  return (
    <button type="button" onClick={onClick} style={{
      borderRadius: 999, padding:"8px 12px", border: `1px solid ${active? "transparent": COLORS.border}`,
      background: active ? `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})` : "#fff",
      color: active ? "#fff" : COLORS.text, fontWeight: 800, cursor:"pointer"
    }}>{children}</button>
  );
}

function Notice({ children }){
  return (
    <div style={{
      background:"#fff", border:`1px solid ${COLORS.border}`, borderRadius:10, padding:12,
      color: COLORS.subtext, marginBottom: 10
    }}>{children}</div>
  );
}

function Card({ children }){
  return (
    <div style={{
      background: COLORS.card, border:`1px solid ${COLORS.border}`, borderRadius:14,
      padding: 18, boxShadow:"0 1px 2px rgba(0,0,0,.05)"
    }}>{children}</div>
  );
}

// ─────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────
const inputStyle = {
  width: "100%",
  height: 44,
  padding: "0 12px",
  marginTop: 6,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  outline: "none",
  background: "#fff",
};

const labelStyle = {
  fontSize: 13,
  color: COLORS.text,
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const linkBtn = {
  border: "none",
  background: "transparent",
  color: COLORS.primary,
  fontWeight: 800,
  cursor: "pointer",
  textDecoration: "none",
};

const solidBtn = {
  height: 42,
  padding: "0 16px",
  borderRadius: 10,
  border: "none",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
};

const primaryBtn = {
  ...solidBtn,
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
};

const ghostBtn = {
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  height: 42,
  padding: "0 16px",
  borderRadius: 10,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
};

const dangerGhostBtn = {
  ...ghostBtn,
  color: COLORS.warn,
  border: `1px solid ${COLORS.warn}33`,
};

const iconBtn = {
  ...ghostBtn,
  width: 36, height: 36, padding: 0, borderRadius: 8
};

const cityPill = {
  border: `1px solid ${COLORS.border}`, background:"#fff", color: COLORS.text,
  borderRadius: 999, padding:"8px 12px", display:"inline-flex", alignItems:"center", gap:6, cursor:"pointer"
};

const modalBackdrop = {
  position: "fixed", inset: 0, background:"rgba(0,0,0,.45)", display:"grid", placeItems:"center", zIndex: 50, padding: 16
};

const modalCard = {
  width: "100%", maxWidth: 640, background:"#fff", border:`1px solid ${COLORS.border}`,
  borderRadius: 14, padding: 16, boxShadow:"0 10px 30px rgba(0,0,0,.25)"
};

const savingMask = {
  position:"fixed", inset:0, background:"rgba(255,255,255,.5)", display:"grid", placeItems:"center",
  fontWeight:900, color: COLORS.text, zIndex: 70
};

const Grid2 = ({ children }) => (
  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap: 12 }}>{children}</div>
);
const Grid3 = ({ children }) => (
  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap: 12 }}>{children}</div>
);
